/**
 * EditFixtureModal — Admin edits fixture schedule (date/time, venue, turf).
 * Backend: PUT /api/v1/events/{event_id}/fixtures/{fixture_id}
 * (Cannot edit fixtures with match_status='completed').
 */
import { useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

// Convert ISO datetime to value acceptable by <input type="datetime-local">
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function EditFixtureModal({ fixture, eventId, onClose, onSaved }) {
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(fixture.scheduled_at));
  const [venueName, setVenueName] = useState(fixture.venue_name || '');
  const [turfName, setTurfName] = useState(fixture.turf_name || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        venue_name: venueName || null,
        turf_name: turfName || null,
      };
      await axiosInstance.put(`/v1/events/${eventId}/fixtures/${fixture.id}`, payload);
      toast.success('Fixture updated');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update fixture');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-3"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-fixture-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-bold text-lg text-[#2B3A67]">Edit Fixture Schedule</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1"
            aria-label="Close"
            data-testid="edit-fixture-close"
          >×</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Match #{fixture.match_number} — {fixture.team_a_name || 'TBD'} vs {fixture.team_b_name || 'TBD'}
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Date &amp; Time</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="edit-fixture-datetime"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Venue</label>
            <input
              type="text"
              value={venueName}
              onChange={e => setVenueName(e.target.value)}
              placeholder="e.g. Mulund Turf"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="edit-fixture-venue"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Turf / Pitch</label>
            <input
              type="text"
              value={turfName}
              onChange={e => setTurfName(e.target.value)}
              placeholder="e.g. Turf A"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="edit-fixture-turf"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
            data-testid="edit-fixture-cancel-btn"
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50"
            data-testid="edit-fixture-save-btn"
          >{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}
